import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import StickoutBanner from '../../shared/stickout-banner';
import Type from '../../shared/fund/type';
import WholesaleTag from '../../shared/fund/wholesale-tag';
import Logo from './primary-content-components/logo';
import Name from './primary-content-components/name';
import Company from './primary-content-components/company';
import Facts from './primary-content-components/facts';
import Actions from './primary-content-components/actions';
import Objective from './primary-content-components/objective';

const Container = styled(StickoutBanner)`
  border: 1px solid ${props => props.theme.secondaryColor30Transparent};
  padding-bottom: 24px;

  ${props => props.theme.isXXLarge && `
    width: 1256px;
    max-width: 1256px;
  `}

  ${props => props.theme.isXLarge && `
    width: calc(100% + 64px);
  `};

  ${props => props.theme.isLarge && `
    margin-top: -155px;
  `};
`;

const Wrapper = styled.div`
  margin: 0;
  padding: 0 20px;
  ${props => props.theme.isLarge && `
    padding: 0 32px;
  `};
  ${props => props.theme.isMobileOrTablet && !props.theme.isMobile && `
    padding: 0 28px;
  `};
`;

const Fund = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  ${props => props.theme.isMobileOrTablet && `
    flex-wrap: wrap;
  `};
  ${props => props.theme.isLarge && `
    max-height: 244px;
  `};
`;

const DescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex-grow: 2;
  ${props => props.theme.isMobileOrTablet && `
    order: 3;
    width: 100%;
  `};
`;

const FlexRow = styled.div`
  display: flex;
  flex-direction: row;
  margin: 28px 0 8px 0;

  > div > span {
    font-size: 14px;
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-image: linear-gradient(-96deg, #fff 31%, #d4dfeb 99%);
  padding: 0;
  margin-top: 24px;
`;

const getGrowth = fund => {
  const attr = _.find(_.get(fund, 'performance'), p => p.name === 'returnSinceInceptionPercent');
  return parseFloat(_.get(attr, 'value'));
};

export default function PrimaryContent({ fund, showInModal, showContactButton }) {
  const factsProps = _.merge(fund, {
    code: fund.apirCode || fund.stockCode,
    // eslint-disable-next-line no-nested-ternary
    codeType: fund.apirCode ? 'apir' : fund.stockCode ? 'stock' : null,
    growth: getGrowth(fund),
  });

  const logoProps = {
    url: fund.company.logoPictureUrl,
    color: fund.company.logoColor,
  };

  return (
    <Container>
      <Wrapper>
        <Fund>
          <Logo {...logoProps} />
          <DescriptionContainer>
            <FlexRow>
              {!!fund.fundType && <Type type={fund.fundType} />}
              {fund.wholesale && <WholesaleTag />}
            </FlexRow>
            <Name name={fund.nameAndStock} />
            {!!fund.company.name && <Company name={fund.company.name} slug={fund.company.slug} />}
          </DescriptionContainer>
          <Actions fund={fund} showInModal={showInModal} showContactButton={showContactButton} />
        </Fund>
        {!!fund.objective && <Objective value={fund.objective} />}
        <Divider />
        <Facts {...factsProps} />
      </Wrapper>
    </Container>
  );
}

PrimaryContent.propTypes = {
  fund: PropTypes.shape({
    id: PropTypes.number.isRequired,
    slug: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    nameAndStock: PropTypes.string.isRequired,
    published: PropTypes.bool.isRequired,
    fundAssetClass: PropTypes.string,
    apirCode: PropTypes.string,
    stockCode: PropTypes.string,
    fundType: PropTypes.string,
    investmentHorizon: PropTypes.string,
    minimumInvestment: PropTypes.number,
    inceptionDate: PropTypes.string,
    objective: PropTypes.string,
    currency: PropTypes.string,
    performance: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      label: PropTypes.string,
      value: PropTypes.string,
    })).isRequired,
    isFollowedByCurrentUser: PropTypes.bool.isRequired,
    company: PropTypes.shape({
      id: PropTypes.number.isRequired,
      slug: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      logoPictureUrl: PropTypes.string,
      logoColor: PropTypes.string,
    }).isRequired,
    fundInvestorVehicleType: PropTypes.string,
    performanceFee: PropTypes.string,
    financialYearEndFeesDate: PropTypes.string,
    wholesale: PropTypes.bool.isRequired,
  }).isRequired,
  showContactButton: PropTypes.bool.isRequired,
  showInModal: PropTypes.bool.isRequired,
};
